import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Button, TrashIcon } from "@dealer-center/core-components";
import { useDeleteAsset } from "@dealer-center/ddc-pages-accountassets-fetch";
import type { ControlcenterAPIInterfaceExternal } from "@dealer-center/ddc-pages-controlcenter-interface";
import type { decodedJwtAndPermissions } from "@dealer-center/ddc-api-user-interface";
import "./Delete.scss";
import { deriveEnvironmentName } from "@dealer-center/core-enviroment";
import { EMPTY_IMAGE } from "../utils/utils";
import { Locale } from "@dealer-center/ddc-core-utils";
import { pathFileTypes } from "@dealer-center/ddc-pages-accountassets-interface";

interface PagesAccountassetsUiDeleteProps {
  clearTrigger?: number;
  handleAlert: (type: string, message: string) => void;
  path: string;
}

export const Delete = ({
  clearTrigger,
  handleAlert,
  path
}: PagesAccountassetsUiDeleteProps): React.ReactElement => {
  const fileType = pathFileTypes[path];
  const altLabel = Locale.getText(
    path === "companylogo" ? "COMPANY_LOGO" : "DEALERSHIP_PHOTO"
  ) as string;

  const { ccUserInfo } = useOutletContext<{
    ccUserInfo: ControlcenterAPIInterfaceExternal;
    userEntity: decodedJwtAndPermissions;
  }>();

  const [deleteResult, setDeleteResult] = useState<{
    type: "success" | "error";
    message: string;
    fileName: string;
  } | null>(null);

  const [foundFilePath, setFoundFilePath] = useState<string | null>(null);
  const [hasImage, setHasImage] = useState(false);
  const deleteAssetMutation = useDeleteAsset();
  const environment = deriveEnvironmentName();
  const baseUrl =
    environment === "prod"
      ? "https://ddc-account-assets.ddcplatform.dealer.com"
      : "https://ddc-account-assets.ddcplatform-np.dealer.com";

  useEffect(() => {
    // Handle clearTrigger changes (Upload button clicked)
    if (clearTrigger !== undefined && clearTrigger > 0) {
      setDeleteResult(null);
      setFoundFilePath(null);
    }
  }, [clearTrigger]);

  useEffect(() => {
    if (deleteResult !== null) {
      handleAlert(deleteResult.type, deleteResult.message);
    }
  }, [deleteResult]);

  const handleImageError = e => {
    e.target.onerror = null;
    e.target.src = EMPTY_IMAGE;
    e.target.title = Locale.getText("NO_IMAGE");
    setHasImage(false);
  };

  const handleDelete = useCallback(async () => {
    if (!ccUserInfo?.accountId || !ccUserInfo?.userId) {
      setDeleteResult({
        type: "error",
        message: "Account ID or User ID not available",
        fileName: ""
      });
      return;
    }

    const fileName = `${path}/${ccUserInfo.accountId}.${fileType}`;

    try {
      const userId = ccUserInfo.internalUserId || ccUserInfo.userId;

      await deleteAssetMutation.mutateAsync({
        asAccount: ccUserInfo.accountId,
        asUser: userId,
        path,
        locale: "en_US"
      });

      setDeleteResult(null);
      setFoundFilePath(null);
    } catch (err) {
      setDeleteResult({
        type: "error",
        message:
          err instanceof Error
            ? err.message
            : (Locale.getText("DELETE_ASSET_ERROR") as string),
        fileName
      });
    }
  }, [
    ccUserInfo?.accountId,
    ccUserInfo?.userId,
    ccUserInfo.internalUserId,
    deleteAssetMutation,
    handleAlert
  ]);

  const canDelete =
    ccUserInfo?.accountId &&
    ccUserInfo?.userId &&
    !deleteAssetMutation.isPending;

  return (
    <>
      <div className="file-preview-container">
        <div className="file-preview-image-block">
          {hasImage && (
            <Button
              analyticsConfiguration={{
                eventName: "PagesAccountassetsUi Delete Asset"
              }}
              buttonStyle="primary"
              disabled={!canDelete}
              icon={<TrashIcon title="Delete icon" />}
              isLoading={deleteAssetMutation.isPending}
              onClick={handleDelete}
            >
              {Locale.getText("DELETE") as string}
            </Button>
          )}
          <img
            alt={altLabel}
            src={`${baseUrl}/${path}/${ccUserInfo.accountId}.${fileType}?t=${Date.now()}`}
            onClick={e => {
              const img = e.target as HTMLImageElement;
              if (!img.src.includes("data:image")) {
                window.open(
                  `${baseUrl}/${path}/${ccUserInfo.accountId}.${fileType}`,
                  "_blank"
                );
              }
            }}
            onError={handleImageError}
            onLoad={e => {
              const img = e.target as HTMLImageElement;
              if (!img.src.includes("data:image")) {
                setHasImage(true);
              }
            }}
          />
        </div>
      </div>
    </>
  );
};
